import { Injectable } from '@angular/core';
import { collection, doc, setDoc, getDoc, updateDoc, deleteDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { AuthService } from './auth.service';
import { BehaviorSubject } from 'rxjs';
import { blankDataModel, blankDataModelSchemaVersion } from 'app/constants/conversation-data-models';
import { environment } from 'env/environment';
import { DEFAULT_GROQ_CHAT_MODEL } from 'app/constants/ai-model-constants';

@Injectable({ providedIn: 'root' })
export class ConversationService {
    private currentConversationId = new BehaviorSubject<string | null>(null);
    currentConversationId$ = this.currentConversationId.asObservable();

    constructor(private firebaseService: FirebaseService, private authService: AuthService) { }

    setCurrentConversationId(id: string) {
        this.currentConversationId.next(id);
    }

    getCurrentConversationId(): string | null {
        return this.currentConversationId.value;
    }

    private getUserId(): string {
        const user = this.authService.getCurrentUser();
        if (!user) throw new Error('User not authenticated.');
        return user.uid;
    }

    async getConversation(conversationId: string) {
        const userId = this.getUserId();
        const ref = doc(this.firebaseService.firestore, `users/${userId}/conversations/${conversationId}`);
        const snapshot = await getDoc(ref);
        return snapshot.exists() ? snapshot.data() : null;
    }

    async createConversation() {
        const userId = this.getUserId();

        // Optionally summarize the previous conversation
        const latestRef = doc(this.firebaseService.firestore, `users/${userId}`);
        const latestSnap = await getDoc(latestRef);
        const latestId = latestSnap.exists() ? latestSnap.data().currentConversationId : null;

        if (latestId) {
            const lastConvoRef = doc(this.firebaseService.firestore, `users/${userId}/conversations/${latestId}`);
            const lastConvoSnap = await getDoc(lastConvoRef);

            if (lastConvoSnap.exists()) {
                const thread = lastConvoSnap.data().thread || [];
                const dataModel = lastConvoSnap.data().dataModel || [];

                // Send to your LLM summarization cloud function
                const summaryResponse = await fetch(`https://${environment.apiBaseURL}.cloudfunctions.net/getAiConversationSummary`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ history: thread, model: DEFAULT_GROQ_CHAT_MODEL, dataModel: dataModel })
                });
 
                const { summary, summaryLabel, timestamp } = await summaryResponse.json();

                // Mark previous conversation inactive and add summary
                await updateDoc(lastConvoRef, {
                    status: 'inactive',
                    aiSummary: summary || 'Unable to Generate Summary',
                    aiSummaryLabel: summaryLabel || 'Unable to Generate Summary Label',
                    summaryTimestamp: timestamp || new Date(),
                    updatedAt: new Date()
                });
            }
        }

        // ➕ Create new conversation
        const newConvoRef = doc(collection(this.firebaseService.firestore, `users/${userId}/conversations`));
        const convoId = newConvoRef.id;
        const now = new Date();
        const newConversation = {
            id: convoId,
            createdAt: now,
            updatedAt: now,
            status: 'active',
            thread: [],
            dataModel: blankDataModel,
            dataModelSchemaVer: blankDataModelSchemaVersion,
            intent: null,
        };

        await setDoc(newConvoRef, newConversation);

        // Update latestConversationId in user profile
        await updateDoc(latestRef, {
            currentConversationId: convoId,
            currentConversationUpdatedAt: now
        });

        return newConversation;
    }

    async updateConversation(conversationId: string, updates: Partial<any>) {
        const userId = this.getUserId();
        const ref = doc(this.firebaseService.firestore, `users/${userId}/conversations/${conversationId}`);
        await updateDoc(ref, {
            ...updates,
            updatedAt: new Date()
        });
    }

    async updateCurrentConversationData(currentThread, dataModel, intent) {
        const userId = this.getUserId();
        const ref = doc(this.firebaseService.firestore, `users/${userId}/conversations/${this.currentConversationId.value}`);
        await updateDoc(ref, {
            thread: currentThread,
            dataModel: dataModel,
            intent: intent,
            updatedAt: new Date()
        });
    }

    async deleteConversation(conversationId: string) {
        const userId = this.getUserId();
        const ref = doc(this.firebaseService.firestore, `users/${userId}/conversations/${conversationId}`);
        await deleteDoc(ref);
    }

    async getLatestConversation() {
        const userId = this.getUserId();
        const ref = doc(this.firebaseService.firestore, `users/${userId}`);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;

        const { currentConversationId } = snap.data();
        return currentConversationId ? this.getConversation(currentConversationId) : null;
    }
}
